<!--
@Author: Kleber Pinel <kleberdasilva>
@Date:   2019-03-01T10:21:35-08:00
@Email:  kleber@hackcapital.com
@Last modified by:  Carmen Chow <carmen@hackcapital.com>
@Last modified time: 2019-04-17 13:09:00
-->

<template lang="pug">
  v-layout(row wrap)
    v-snackbar(:value='customFieldsError' color='error')
      span {{ customFieldsErrorMessage }}

    content-picker(
      :visible="showLDContentModal"
      @close="hideLDContent"
      @insert="insertLDContent"
    )

    image-properties(
      :visible="showImageModal"
      :value="''"
      :maxSizeInMB="maxSizeInMB"
      @close="hideImageContent"
      @insert="insertImage"
      title="Insert an image"
    )

    v-layout.justify-end(v-if="actionsBar" row wrap px-1 ma-0)
      div.action-item
        slot(name="choose-template")
      div.action-item
        slot(name="insert-video")
      div.action-item(@click.stop="showImageContent")
        slot(name="insert-image")
      div.action-item(@click.stop="showLDContent")
        // slot if want to put activator btn above input field
        slot(name="insert-content")
      div.action-item
        slot(name="action-one")

    v-layout.justify-space-between(v-if="!hideActivator")
      v-flex.mt-1.ml-5.pa-0.d-flex.align-center(v-if="$slots.suggestion")
        slot(name="suggestion")
      v-flex(:style="activatorStyle" text-xs-right).mt-1.pa-0
        v-flex.pa-0(:class="activatorSize")
          a(@click.stop="showLDContent")
            slot(name="activator-top-texts-insert-content")
            slot(name="activator-top-texts-insert-video")
          a(@click.stop="showImageContent")
            slot(name="activator-top-texts-insert-image")

    v-flex(:class="inputSize").height-100
      v-textarea(
        data-cy="message-body-textarea"
        v-if="fieldType === 'textarea'"
        :value="value"
        @input="onInput"
        @blur="saveCaretData(input)"
        :label="label"
        :outline="outlineField"
        :ref="refName"
        :rows="rows"
        :no-resize="noResize"
        :disabled="disabled"
        prepend-icon="subject"
        hide-details
      )

      v-text-field(
        data-cy="message-body"
        v-if="fieldType === 'input'"
        :value="value"
        @input="onInput"
        @blur="saveCaretData(input)"
        :label="label"
        :outline="outlineField"
        :ref="refName"
        :disabled="disabled"
        :box="box"
        :prepend-icon="icon"
      )

      rich-textarea-editor(
        :style="cssStyle"
        v-if="fieldType === 'quill'"
        :attachment-handler-id="attachmentHandlerId"
        :value="value"
        :disabled="disabled"
        @input="onInput"
      )
    v-flex(:style="activatorStyle" v-if="isEmailSubject").mt-3.shrink
      //- here in case the activator should go below the input field
      v-flex.pt-0.pr-0(
        :class="activatorSize"
        @click.stop="showLDContent"
      )
        slot(name="activator-bottom-insert-content")

</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import TitleBar from '@/Core/components/TitleBar';
import RichTextareaEditor from '@/Core/components/richTextareaEditor/RichTextEditor';
import ContentPicker from '@/Core/components/richTextareaEditor/ContentPicker';
import caretPosition from '@/Core/mixins/caret-positioning';
import ImageProperties from '@/Core/components/richTextareaEditor/ImageProperties';

const { mapState } = createNamespacedHelpers('communicationsCustomFields');
const {
  mapState: mapConversationsState,
  mapMutations: mapConversationsMutations,
} = createNamespacedHelpers('conversations');

export default {
  components: {
    TitleBar,
    ContentPicker,
    RichTextareaEditor,
    ImageProperties,
  },
  mixins: [caretPosition],
  props: {
    attachmentHandlerId: {
      type: String,
      default: '',
    },
    hideActivator: {
      type: Boolean,
      default: false,
    },
    label: String,
    value: {
      type: String,
      default: '',
    },
    fieldType: String,
    refName: String,
    disabled: {
      type: Boolean, // disable field
      default: false,
    },
    // vuetify input styles
    outlineField: Boolean, // add border to input
    icon: String,
    box: Boolean,
    rows: String,
    noResize: Boolean,
    inputSize: {
      type: String,
      default: 'xs12',
    },
    activatorSize: {
      type: String,
      default: 'xs12',
    },
    actionsBar: {
      type: Boolean,
      default: false,
    },
    activatorStyle: {
      default: '',
    },
    containerHeight: {
      default: '',
    },
    isEmailSubject: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showLDContentModal: false,
      showImageModal: false,
      maxSizeInMB: 5,
    };
  },
  watch: {
    value(newValue) {
      if (this.input) this.saveCaretData(this.input);
    },
  },
  computed: {
    ...mapState({
      customFieldsError: state => state.error,
    }),
    ...mapConversationsState(['textMessageElements']),
    input() {
      if (this.$refs[this.refName]) {
        return this.$refs[this.refName].$refs.input;
      }
    },
    customFieldsErrorMessage() {
      if (this.customFieldsError) {
        return `${this.customFieldsError.service}: ${this.customFieldsError.message}`;
      }
      return null;
    },
    inputText: {
      get() {
        return this.value;
      },
      set(newValue) {
        return newValue;
      },
    },
    cssStyle() {
      return `min-height: ${this.containerHeight}%`;
    },
  },
  methods: {
    ...mapConversationsMutations(['setTextImages']),
    onInput(newValue) {
      this.$emit('input', newValue);
    },
    insertLDContent($event) {
      this.hideLDContent();
      this.formatContent($event);
    },
    showLDContent() {
      this.showLDContentModal = true;
    },
    hideLDContent() {
      this.showLDContentModal = false;
    },
    showImageContent() {
      this.showImageModal = true;
    },
    hideImageContent() {
      this.showImageModal = false;
    },
    insertImage(imageData) {
      const imageAlias = this.generateImageAlias();
      const img = {
        alias: imageAlias,
        url: imageData.src,
      };
      this.setTextImages(img);
      this.formatContent(imageAlias);
      this.showImageModal = false;
    },
    formatContent(text) {
      this.input.focus();
      const cursorIndex = this.input.selectionEnd;
      let message = this.value.substring(0, cursorIndex) + text + this.value.substring(cursorIndex);
      this.onInput(message);
    },
    generateImageAlias() {
      const timeStamp = new Date().getTime() + Math.floor(Math.random() * 100);
      return `{{img_media_${timeStamp}}}`;
    },
  },
};
</script>

<style scoped>
.action-item {
  width: auto;
}

.edit-html {
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 13px;
}

.edit-html >>> textarea {
  margin: 0px 0px;
  height: calc(100vh - 500px);
}

.height-100 {
  height: 100%;
  width: 0;
}
</style>
